import { type EndpointResponse } from '@owner/olympus-client'
import { useLocalStorage } from '@vueuse/core'
import { isBefore } from 'date-fns'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed, onScopeDispose, ref } from 'vue'
import { type RouteLocationNamedRaw } from 'vue-router'

import { clearAllLaunchComposerDrafts } from '@/features/launch/composables/useLaunchComposerDrafts'
import { logError, logInfo } from '@/logger'
import { OlympusClient } from '@/OlympusClient'
import { useFlag } from '@/utils/featureFlagStore'
import { useFeatureFlags } from '@/utils/useFeatureFlags'

import { AUTH_STORAGE_KEYS, persistAuthStorageKey } from './authStorageKeys'
import { clearIframeSession, isInsideIframe } from './iframeSession'
import {
  type User,
  type BrandSession,
  type SessionResponse,
  type Session,
  type AuthLogoutOptions,
} from './types'
import {
  getAuthorizer,
  resetAuthorizer,
  safeInitializeAuthorizer,
} from './useAuthorizer'

// Kill switch for the `/login` dead-end logout. Off → legacy redirect.
export const LOGIN_REDIRECT_LOOP_GUARD_FLAG = 'hermes.loginRedirectLoopGuard'

// Cross-tab isolation stopped mirroring auth-key `storage` events entirely, so a
// sibling window's *refreshed* session (and its new CSRF token) never reaches
// this window — its stale token then 403s and forces a logout. On → re-apply
// only *non-null* sibling writes to the auth keys (clears stay ignored, so the
// isolation win holds). Off → no sync, current behavior.
export const CROSS_TAB_SESSION_SYNC_FLAG = 'hermes.crossTabSessionSync'

const AUTH_BROADCAST_CHANNEL = 'owner-auth'
interface AuthBroadcastMessage {
  type: 'logout'
}

export const useAuthStore = defineStore('auth', () => {
  const featureFlags = useFeatureFlags()
  const embedAuthIsolated = isInsideIframe()

  const otpSessionId = useLocalStorage<string | null>(
    persistAuthStorageKey(AUTH_STORAGE_KEYS.otpSessionId),
    null
  )

  const setOtpSessionId = (sessionId: string): void => {
    otpSessionId.value = sessionId
  }

  // Route getters to the appropriate store
  const isMultiBrandUser = useLocalStorage<boolean>(
    persistAuthStorageKey(AUTH_STORAGE_KEYS.isMultiBrandUser),
    false
  )

  const isLocationScopedUser = computed(() => {
    if (!brandSession.value) return false

    return (
      !!brandSession.value.locationIds &&
      brandSession.value.locationIds.length > 1
    )
  })

  const hasAllLocationsAccess = computed(() => {
    if (!brandSession.value) return false

    return brandSession.value.hasAllLocationsAccess ?? false
  })

  const brandSession = useLocalStorage<BrandSession | null>(
    persistAuthStorageKey(AUTH_STORAGE_KEYS.brandSession),
    null,
    {
      listenToStorageChanges: false,
      serializer: {
        read: (v) => (v ? JSON.parse(v) : null),
        write: (v) => JSON.stringify(v),
      },
    }
  )

  const csrfToken = computed<string | null>(() => {
    if (!session.value) return null

    return session.value.csrfToken ?? null
  })

  const scopes = computed(() => {
    if (!brandSession.value) return []

    return brandSession.value.scopes
  })

  const expiresAt = computed(() => {
    if (!session.value) return null

    return session.value.expires
  })

  const session = useLocalStorage<Session | null>(
    persistAuthStorageKey(AUTH_STORAGE_KEYS.session),
    null,
    {
      listenToStorageChanges: false,
      serializer: {
        read: (v) => (v ? JSON.parse(v) : null),
        write: (v) => JSON.stringify(v),
      },
    }
  )

  // New V2 user storage
  const user = useLocalStorage<User | null>(
    persistAuthStorageKey(AUTH_STORAGE_KEYS.user),
    null,
    {
      listenToStorageChanges: false,
      serializer: {
        read: (v) => (v ? JSON.parse(v) : null),
        write: (v) => JSON.stringify(v),
      },
    }
  )

  // Persisted so the session-restore path in `checkAuthenticated` re-creates
  // the same Authorizer provider chosen at login (otherwise refresh always
  // falls back to Olympus and ignores the auth-service `partner_at` cookie).
  const sessionProvider = useLocalStorage<'olympus' | 'auth-service' | null>(
    persistAuthStorageKey(AUTH_STORAGE_KEYS.sessionProvider),
    null
  )

  // Login timestamp, stored separately because `Session` is a generated
  // olympus-client type we don't own. Persisted so it survives session restore
  // (refresh / new tab) and the logout funnel can always report how long the
  // session actually lasted (`sessionAgeMs`).
  const loginAt = useLocalStorage<string | null>(
    persistAuthStorageKey(AUTH_STORAGE_KEYS.loginAt),
    null
  )

  const acl = computed(() => {
    if (!brandSession.value)
      return {
        brands: [],
        locations: [],
      }

    return {
      brands: brandSession.value.brandId ? [brandSession.value.brandId] : null,
      locations: brandSession.value.locationIds,
    }
  })

  const role = computed(() => {
    if (!brandSession.value) return null

    return brandSession.value.role
  })

  const isSuperAdmin = computed(() => {
    if (!role.value) return false

    return role.value === 'pb:super'
  })

  const home = computed<RouteLocationNamedRaw>(() => {
    if (!user.value) return { name: 'auth.login' }

    if (isSuperAdmin.value) return { name: 'brands' }

    if (brandSession.value?.brandId) {
      return {
        name: 'brand.home',
        params: {
          brandId: brandSession.value.brandId,
        },
      }
    }

    return { name: 'auth.login' }
  })

  const isAuthenticated = computed(() => {
    if (!user.value || !session.value) return false

    try {
      const now = new Date()
      const expires = new Date(session.value.expires)
      const isExpired = isBefore(expires, now)
      if (isExpired) {
        logError(new Error('Auth session expired'), {
          context: 'useAuthStore',
          expires,
          now,
        })
      }
      return !isExpired
    } catch (error) {
      return false
    }
  })

  // Diagnostic: record the last cross-tab `storage` event that cleared an auth
  // key. `useLocalStorage` mirrors `storage` events, so another tab (or app on
  // the same origin) writing `owner-session-v2`/`owner-user-v2` to null flips
  // this tab's `user`/`session` refs — and therefore `isAuthenticated` — with
  // no in-tab call and no network request, matching the premature-logout RUM
  // signature. A JS stack can't attribute a browser event, so we capture the
  // event itself instead. Remove once the auth-service cause is found.
  let lastAuthStorageClear: {
    key: string
    capturedAtMs: number
  } | null = null

  // Cross-tab values are untrusted input, so a malformed write is dropped rather
  // than assigned. The `x is Session` guard narrows without any cast — `in`
  // narrows the property access — and TS carries the `Session` type to every
  // caller once it returns true.
  const isSession = (value: unknown): value is Session =>
    typeof value === 'object' &&
    value !== null &&
    'csrfToken' in value &&
    typeof value.csrfToken === 'string' &&
    'expires' in value &&
    typeof value.expires === 'string'

  const parseRemoteSession = (rawValue: string): Session | null => {
    let parsed: unknown
    try {
      parsed = JSON.parse(rawValue)
    } catch {
      return null
    }
    return isSession(parsed) ? parsed : null
  }

  // Cross-tab sync applies a sibling window's *non-null* write to
  // `owner-session-v2` into this window's ref. Scoped to the session key only:
  // it carries the CSRF token, which rotates on every login and is the value
  // that goes stale across windows and 403s. User/brand identity stay isolated —
  // syncing them would swap this window's identity out from under a cached
  // authorizer. Clears (null) are never applied, preserving cross-tab isolation.
  if (typeof window !== 'undefined' && !embedAuthIsolated) {
    window.addEventListener('storage', (event) => {
      const isAuthKey =
        event.key === AUTH_STORAGE_KEYS.session ||
        event.key === AUTH_STORAGE_KEYS.user
      if (!isAuthKey || event.key === null) return

      // newValue null = key removed/cleared in another tab.
      if (event.newValue === null) {
        lastAuthStorageClear = {
          capturedAtMs: Date.now(),
          key: event.key,
        }
        return
      }

      if (
        event.key === AUTH_STORAGE_KEYS.session &&
        featureFlags.isEnabled(CROSS_TAB_SESSION_SYNC_FLAG)
      ) {
        const remoteSession = parseRemoteSession(event.newValue)
        if (remoteSession) session.value = remoteSession
      }
    })
  }

  // Diagnostic snapshot of the raw auth state behind `isAuthenticated`, read
  // without mutating anything. `isauthenticated-false` logouts fire when this
  // flips false while the session is still valid (`sessionValid: true`) — this
  // pins down which ref went null (user vs session), via which provider, and
  // whether a cross-tab storage clear immediately preceded it, so the
  // `auth.logout` funnel can attribute an otherwise-invisible client-state
  // de-auth. Remove once the auth-service premature-logout cause is found.
  const authStateSnapshot = computed(() => {
    const sessionValue = session.value
    let sessionValid: boolean | null = null
    if (sessionValue) {
      try {
        sessionValid = !isBefore(new Date(sessionValue.expires), new Date())
      } catch {
        sessionValid = null
      }
    }

    // How long ago the last cross-tab auth-key clear fired. A small value here
    // when isAuthenticated flips false is strong evidence of a cross-tab clear.
    const storageClearAgeMs = lastAuthStorageClear
      ? Date.now() - lastAuthStorageClear.capturedAtMs
      : null

    return {
      hasBrandSession: !!brandSession.value,
      hasSession: !!sessionValue,
      hasUser: !!user.value,
      lastStorageClearKey: lastAuthStorageClear?.key ?? null,
      sessionProvider: sessionProvider.value,
      sessionValid,
      storageClearAgeMs,
    }
  })

  // Not persisted: starts false on every session restore so the transient
  // pre-recovery window isn't mistaken for a terminal dead end.
  const brandSessionRecoverySettled = ref(false)

  // Local teardown shared by `logout()` and the broadcast handler. `logout()`
  // additionally owns the RUM funnel + network request; this is state-only.
  const clearLocalAuthState = (): void => {
    resetAuthorizer() // clear cached grants before isAuthenticated flips false
    session.value = null
    user.value = null
    brandSession.value = null
    isMultiBrandUser.value = false
    otpSessionId.value = null
    sessionProvider.value = null
    loginAt.value = null
    // Reset so a same-tab re-login re-runs brand recovery from scratch.
    brandSessionRecoverySettled.value = false
    // Module-level draft store outlives navigation; clear it so the next
    // specialist on this tab doesn't inherit unsent drafts.
    clearAllLaunchComposerDrafts()
  }

  // Intentional logout doesn't cross tabs via `storage` anymore (mirroring is
  // off), so coordinate it here: `logout()` posts, other tabs tear down.
  let handlingRemoteLogout = false
  let authBroadcastChannel: BroadcastChannel | null = null
  if (typeof BroadcastChannel !== 'undefined' && !embedAuthIsolated) {
    authBroadcastChannel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL)
    authBroadcastChannel.onmessage = (
      event: MessageEvent<AuthBroadcastMessage>
    ): void => {
      if (event.data?.type !== 'logout') return
      // Teardown flips isAuthenticated, so useSessionManager's watcher will call
      // logout(); arm this so it no-ops instead of echoing the broadcast back.
      // Only when authenticated: otherwise no transition fires and the flag
      // would leak onto the next real logout.
      if (isAuthenticated.value) handlingRemoteLogout = true
      clearLocalAuthState()
    }
    onScopeDispose(() => {
      authBroadcastChannel?.close()
      authBroadcastChannel = null
    })
  }
  const broadcastLogout = (): void => {
    const message: AuthBroadcastMessage = { type: 'logout' }
    authBroadcastChannel?.postMessage(message)
  }

  const loginRedirectLoopGuardEnabled = useFlag(LOGIN_REDIRECT_LOOP_GUARD_FLAG)

  // Terminal dead end: authenticated, `home` bounces to login, recovery settled,
  // and no accessible brand remains. Logging out is right here — redirecting
  // would loop `/login` onto itself (the freeze). Excludes the pre-settle race.
  const homeResolvesToDeadEndLogin = computed(() => {
    if (!loginRedirectLoopGuardEnabled.value) return false

    return (
      home.value.name === 'auth.login' &&
      isAuthenticated.value &&
      brandSessionRecoverySettled.value &&
      !brandSession.value
    )
  })

  // Helper to set session data from V2 response
  const setSession = ({
    csrfToken,
    defaultBrandSession,
    expiresAt,
    sessionProvider: newSessionProvider,
    user: sessionUser,
    isMultiBrandUser: newIsMultiBrandUserValue = false,
  }: SessionResponse): void => {
    session.value = {
      csrfToken,
      expires: expiresAt,
      scopes: defaultBrandSession.scopes,
    }

    user.value = sessionUser
    brandSession.value = defaultBrandSession
    isMultiBrandUser.value = newIsMultiBrandUserValue
    sessionProvider.value = newSessionProvider ?? null
    loginAt.value = new Date().toISOString()
  }

  // Brand permissions are loaded after the parent session has been installed.
  const setEmbeddedSession = ({
    user: sessionUser,
    expiresAt,
    csrfToken,
  }: {
    user: User
    expiresAt: string
    csrfToken?: string
  }): void => {
    session.value = {
      csrfToken: csrfToken ?? '',
      expires: expiresAt,
      scopes: [],
    }
    user.value = sessionUser
    isMultiBrandUser.value = false
    sessionProvider.value = csrfToken ? 'auth-service' : 'olympus'
    loginAt.value = new Date().toISOString()
  }

  const setBrandSession = (newBrandSession: BrandSession): void => {
    brandSession.value = newBrandSession
  }

  // Re-initialize the Authorizer for the current brand session. The Authorizer
  // bakes the brandId into its provider at construction time, so on brand
  // switch we must destroy the old instance and build a new one.
  const reinitializeAuthorizer = async (context: string): Promise<void> => {
    if (!user.value || !brandSession.value) return

    resetAuthorizer()
    await safeInitializeAuthorizer(
      user.value,
      brandSession.value,
      context,
      sessionProvider.value ?? undefined,
      () => csrfToken.value
    )
  }

  // The active brand is persisted in localStorage and reused on every session
  // restore (refresh / new tab). If the user's access to that brand was revoked
  // since it was written, redirecting to it would fire `/core` and 403 on every
  // boot (tripping the RBAC monitor). Once the authorizer has loaded the
  // caller's access for the persisted brand, drop the brand if it is no longer
  // readable and fall back to an accessible one — the same list the brand
  // picker uses. No-op, and no extra request, on the happy path where the
  // persisted brand is still accessible.
  //
  // Returns true when the brand session was swapped or cleared, so the caller
  // can redirect to the resolved `home` instead of letting navigation continue
  // to the stale `/brands/<id>/...` URL.
  const ensureAccessibleBrandSession = async (): Promise<boolean> => {
    if (!user.value || !brandSession.value) return false

    const authorizer = getAuthorizer()
    // Authorizer failed to initialize (logged upstream) — leave the session
    // alone so the existing `/core` error handling can recover.
    if (!authorizer) return false

    try {
      if (authorizer.can('read', 'brand')) {
        // Persisted brand is accessible — settled, not a dead end.
        brandSessionRecoverySettled.value = true
        return false
      }
    } catch {
      // Transient init failure — leave the session alone. Intentionally leaves
      // `brandSessionRecoverySettled` false so this isn't read as a dead end.
      return false
    }

    const staleBrandId = brandSession.value.brandId

    let fallbackBrand
    try {
      const response =
        await OlympusClient.users.v1.users.user.brands.paginateBrands({
          params: { userId: user.value.id },
          query: {
            highestSubscriptionTier: ['standard', 'website', 'none'],
            limit: 1,
            sort: ['updatedAt:desc'],
          },
        })
      fallbackBrand = response.results[0]
    } catch (error) {
      // Keep the stale session so `/core` error handling can recover.
      // Intentionally leaves `brandSessionRecoverySettled` false — a network
      // failure isn't a settled "no accessible brand" verdict.
      logError(error, { context: 'ensureAccessibleBrandSession' })
      return false
    }

    // The persisted brand is itself the most-recently-updated accessible
    // brand — nothing to recover. Signalling a redirect here would bounce to a
    // `home` that resolves to this same brand while the authorizer is still
    // bound to the pre-swap context, so `can('read','brand')` stays false and
    // the guard re-enters indefinitely (CORE-330). Treat a no-op swap as a
    // no-op: no redirect, no event.
    if (fallbackBrand?.id === staleBrandId) return false

    logInfo('auth.stale_brand_session.recovered', {
      cleared: !fallbackBrand,
      fallbackBrandId: fallbackBrand?.id ?? null,
      staleBrandId,
    })
    // Point the session at an accessible brand; `loadBrand` repopulates the
    // full session (role / scopes / locations) from `/core` on navigation.
    // No accessible brand at all → clear so `home` routes to login.
    brandSession.value = fallbackBrand
      ? { ...brandSession.value, brandId: fallbackBrand.id }
      : null

    // Settled: swapped brand, or (fallbackBrand null) the terminal dead end.
    brandSessionRecoverySettled.value = true

    // Re-bind the authorizer to the resolved brand. The caller redirects to
    // `home` and re-runs this guard before `loadBrand` reinitializes the
    // authorizer, so without this the next `can('read','brand')` still reflects
    // the inaccessible brand and re-enters recovery. Best-effort: the swap is
    // already committed and `loadBrand` rebuilds the authorizer on arrival, so
    // a rebuild failure here must not undo the swap or block the redirect.
    if (fallbackBrand) {
      await reinitializeAuthorizer('ensureAccessibleBrandSession').catch(
        (error) => {
          logError(error, { context: 'ensureAccessibleBrandSession' })
        }
      )
    }
    return true
  }

  const setUserPhone = (phone: string): void => {
    if (user.value) {
      user.value = {
        ...user.value,
        phone,
      }
    }
  }

  const setUser = (userUpdate: Partial<User>): void => {
    if (user.value) {
      user.value = {
        ...user.value,
        ...userUpdate,
      }
    }
  }

  // Route actions to the appropriate store
  const login = async ({
    email,
    password,
  }: {
    email: string
    password: string
  }): Promise<
    | { status: 'success' }
    | Extract<
        EndpointResponse<'auth.v2.hermes.login'>,
        { status: 'requires-2fa' }
      >
  > => {
    const response = await OlympusClient.auth.v2.hermes.login({
      headers: {
        Authorization: `Basic ${btoa(`${email}:${password}`)}`,
      },
    })

    if (response.status === 'success') {
      setSession(response)
      await safeInitializeAuthorizer(
        response.user,
        response.defaultBrandSession,
        'login',
        response.sessionProvider,
        () => csrfToken.value
      )

      return { status: 'success' }
    } else if (response.status === 'requires-2fa') {
      otpSessionId.value = response.sessionId

      return response
    } else {
      throw new Error('Login failed')
    }
  }

  const resendOtp = async ({
    sessionId,
    method,
    mfaToken,
  }: {
    sessionId?: string | null
    method: 'email' | 'phone'
    mfaToken?: string
  }): Promise<
    Extract<EndpointResponse<'auth.v2.hermes.resendOtp'>, { status: 'success' }>
  > => {
    if (!sessionId) sessionId = otpSessionId.value

    if (!sessionId) throw new Error('Session ID is required for V2 OTP resend')

    const response = await OlympusClient.auth.v2.hermes.resendOtp({
      body: { method, mfaToken, sessionId },
    })

    if (response.status === 'success') {
      otpSessionId.value = response.sessionId
      return response
    } else {
      throw new Error(response.message)
    }
  }

  const resetPassword = async ({
    password,
    token,
  }: {
    password: string
    token: string
  }): Promise<void> => {
    // Use the V2 resetPassword endpoint
    const response = await OlympusClient.auth.v2.hermes.resetPassword({
      body: {
        password,
      },
      headers: {
        Authorization: `Otp ${token}`,
      },
    })

    // Set the session using the V2 response format
    setSession({
      ...response,
      status: 'success',
    })
    await safeInitializeAuthorizer(
      response.user,
      response.defaultBrandSession,
      'resetPassword',
      response.sessionProvider,
      () => csrfToken.value
    )
  }

  const completeCredentialSetup = async ({
    setupToken,
    email,
    password,
  }: {
    setupToken: string
    email: string
    password: string
  }): Promise<
    | { status: 'success' }
    | { status: 'no-session' }
    | Extract<
        EndpointResponse<'auth.v2.hermes.completeCredentialSetup'>,
        { status: 'requires-2fa' }
      >
  > => {
    const response = await OlympusClient.auth.v2.hermes.completeCredentialSetup(
      {
        body: {
          email,
          password,
          setupToken,
        },
      }
    )

    if (response.status === 'success') {
      // A surface-restricted account completes setup but is barred from a hermes
      // session, so the endpoint returns success with no session payload.
      if (!('user' in response)) {
        return { status: 'no-session' }
      }

      setSession(response)
      await safeInitializeAuthorizer(
        response.user,
        response.defaultBrandSession,
        'credentialSetup',
        response.sessionProvider,
        () => csrfToken.value
      )

      return { status: 'success' }
    } else if (response.status === 'requires-2fa') {
      otpSessionId.value = response.sessionId

      return response
    } else {
      throw new Error('Credential setup failed')
    }
  }

  const multiFactorLogin = async ({
    sessionId,
    code,
    mfaToken,
  }: {
    sessionId?: string | null
    code: string
    mfaToken?: string
  }): Promise<void> => {
    if (!sessionId) sessionId = otpSessionId.value

    if (!sessionId)
      throw new Error(
        'Session ID is required for V2 multi-factor authentication'
      )

    const response = await OlympusClient.auth.v2.hermes.verifyOtp({
      body: { mfaToken },
      headers: {
        Authorization: `Otp ${btoa(`${code}:${sessionId}`)}`,
      },
    })

    setSession({
      ...response,
      status: 'success',
    })
    await safeInitializeAuthorizer(
      response.user,
      response.defaultBrandSession,
      'multiFactorLogin',
      response.sessionProvider,
      () => csrfToken.value
    )

    otpSessionId.value = null
  }

  // Deduplicates concurrent logout calls (e.g. multiple 401s on page load)
  let logoutInProgress: Promise<void> | null = null

  /**
   * Ends the session locally and on the server.
   *
   * @param options Why the logout happened, plus diagnostic detail for the
   *   `auth.logout` funnel.
   * @returns Whether this call is the one that ended a live session. False when
   *   the logout is already owned elsewhere — a concurrent in-flight call, a
   *   sibling tab's broadcast, or a session that was already empty. Callers use
   *   it to run once-per-logout side effects (analytics) exactly once, since
   *   several triggers converge on this teardown.
   */
  const logout = async (options?: AuthLogoutOptions): Promise<boolean> => {
    // Watcher-driven logout on a tab that already tore down from a received
    // broadcast: state is clean and the originating tab owned the RUM/network/
    // broadcast, so no-op rather than echo it back.
    if (handlingRemoteLogout) {
      handlingRemoteLogout = false
      return false
    }

    if (logoutInProgress) {
      // Rejection handling is the primary caller's responsibility
      await logoutInProgress.catch(() => undefined)
      return false
    }

    // Read before the teardown nulls it. Deliberately not `isAuthenticated`: an
    // expired session is still a live one to end, and that is precisely the
    // session-timer-expired logout.
    const endedLiveSession = !!user.value || !!session.value

    logoutInProgress = (async () => {
      // Single chokepoint every logout passes through: record why it happened
      // and how long the session lasted, before the session is torn down.
      // `sessionAgeMs` is the headline signal for "logged out too early"
      // reports — it needs no client-side repro.
      const now = new Date()
      const expiresValue = session.value?.expires ?? null
      const loginValue = loginAt.value
      logInfo('auth.logout', {
        // Diagnostic context first so the core telemetry fields below stay
        // authoritative — a stray `reason`/`userId`/`sessionAgeMs` in `detail`
        // must never clobber the headline metrics this event exists to produce.
        ...options?.detail,
        brandId: brandSession.value?.brandId ?? null,
        expired: expiresValue ? isBefore(new Date(expiresValue), now) : null,
        expiresAt: expiresValue,
        loginAt: loginValue,
        path:
          typeof window !== 'undefined' ? window.location?.pathname : undefined,
        reason: options?.reason ?? 'unknown',
        sessionAgeMs: loginValue
          ? now.getTime() - new Date(loginValue).getTime()
          : null,
        sessionProvider: sessionProvider.value,
        userId: user.value?.id ?? null,
      })

      // Capture the CSRF token before clearing the session so the logout
      // request can still authenticate, but unset the session first to avoid
      // re-entrant logout calls triggered by in-flight requests observing a
      // still-valid session (see PR #36913 logout storm).
      const csrfTokenValue = csrfToken.value

      clearLocalAuthState()

      // Embed auth is bearer-only. Cookie logout + BroadcastChannel would
      // tear down the real dashboard tab that shares this origin.
      if (embedAuthIsolated) {
        clearIframeSession()
        return
      }

      // Tell sibling tabs. No-op where BroadcastChannel is unsupported.
      broadcastLogout()

      await OlympusClient.auth.v2.hermes
        .logout({
          headers: {
            'X-CSRFToken': csrfTokenValue ?? undefined,
          },
        })
        .catch(() => undefined)
    })()

    try {
      await logoutInProgress
    } finally {
      logoutInProgress = null
    }

    return endedLiveSession
  }

  const requireUser = (): User => {
    if (!user.value) {
      throw new Error('User not found')
    }

    return user.value
  }

  return {
    acl,
    authStateSnapshot,
    brandSession,
    brandSessionRecoverySettled,
    completeCredentialSetup,
    csrfToken,
    ensureAccessibleBrandSession,
    expiresAt,
    hasAllLocationsAccess,
    home,
    homeResolvesToDeadEndLogin,
    isAuthenticated,
    isLocationScopedUser,
    isMultiBrandUser,
    isSuperAdmin,
    login,
    logout,
    multiFactorLogin,
    reinitializeAuthorizer,
    requireUser,
    resendOtp,
    resetPassword,
    role,
    scopes,
    sessionProvider,
    setBrandSession,
    setEmbeddedSession,
    setOtpSessionId,
    setSession,
    setUser,
    setUserPhone,
    user,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot))
}
